import { assetFetchInit, assetUrl } from './assetUrl.js';

/**
 * iOS-13-safe Web Audio sound manager.
 *
 * Why not HTMLAudio? On iOS, <audio> elements have very poor concurrent-playback
 * behavior (only one stream can effectively run; latency is high; and they're
 * subject to aggressive throttling when the page is backgrounded). Web Audio
 * solves all three: a single AudioContext can mix many AudioBufferSourceNodes
 * with sub-frame latency, and decoded buffers replay instantly with no GC churn.
 *
 * iOS specifics handled here:
 *   - `webkitAudioContext` fallback for iOS 12/13 Safari.
 *   - Suspended-by-default AudioContext is resumed inside a real user gesture
 *     (pointerdown/touchstart/touchend/mousedown/keydown — capture phase).
 *   - Silent-buffer "unlock" trick still needed on iOS 12/13 to enable playback.
 *   - `decodeAudioData` is wrapped to support both promise + callback forms
 *     (iOS 12 only supports the callback form).
 *   - Sequential decode during preload to avoid the memory spike that crashes
 *     mobile Safari when many MP3s decode in parallel.
 *   - `visibilitychange` resume so the context wakes up after backgrounding.
 *
 * Every play() creates a throwaway AudioBufferSourceNode — these are GC-cheap
 * and explicitly designed for high-frequency triggering. A short per-key
 * throttle prevents accidental machine-gun overlap on rapid taps / reel stops.
 */

const SOUND_PATHS = {
  bg: 'assets/sounds/music/background_music.mp3',
  spinStart: 'assets/sounds/reel/reel_start_button.mp3',
  // spinStop → same clip as spinStart (see _bufferKeyFor)
  reelSpin: 'assets/sounds/reel/reel_spin_sound.mp3',
  reelDefaultStop: 'assets/sounds/reel/reel_default_stop.mp3',
  reelStopper: 'assets/sounds/reel/reel_stopper.mp3',
  winFrame: 'assets/sounds/reel/win_frame_sound.mp3',
  expandingWilds: 'assets/sounds/reel/expanding_wilds.mp3',
  bigWin: 'assets/sounds/popups/big_win_popup.mp3',
  bigWinAlert: 'assets/sounds/popups/big_win_alert_sound.mp3',
  hugeWin: 'assets/sounds/popups/huge_win_popup.mp3',
  megaWin: 'assets/sounds/popups/mega_win_popup.mp3',
  epicWin: 'assets/sounds/popups/epic_win_popup.mp3',
  popupOut: 'assets/sounds/popups/popup_out.mp3',
  win: 'assets/sounds/symbols/win.mp3',
  wild: 'assets/sounds/symbols/wild.mp3',
  scatter: 'assets/sounds/symbols/scatter.mp3',
  coin: 'assets/sounds/symbols/coin.mp3',
  uiClick: 'assets/sounds/ui/ui_click.mp3',
  uiError: 'assets/sounds/ui/ui_error.mp3',
  uiPopupOpen: 'assets/sounds/ui/ui_popup_open.mp3',
  uiPopupClose: 'assets/sounds/ui/ui_popup_close.mp3',
  uiSpinSpeed: 'assets/sounds/ui/ui_spin_speed.mp3',
};

/** Background music bus — kept separate from all one-shots. */
const MUSIC_BUS_VOLUME = 0.3;

/** Shared bus for every non-music sound (UI, reels, symbols, win popups). */
const ONESHOT_BUS_VOLUME = 0.8;

/** Default per-clip gain on the one-shot bus (1 = full bus level). */
const DEFAULT_ONESHOT_GAIN = 1;

/**
 * Per-key multipliers on top of {@link ONESHOT_BUS_VOLUME}. Omit = default.
 * Start/stop button MP3s are mastered hotter — pull them down to match the mix.
 */
const SOUND_VOLUMES = {
  spinStart: 0.55,
  spinStop: 0.55,
  reelSpin: 0.65,
  reelDefaultStop: 0.5,
  reelStopper: 1.55,
  winFrame: 0.3,
  win: 0.85,
  wild: 0.85,
  bigWinAlert: 0.9,
  scatter: 0.45,
  coin: 0.65,
};

/** Default minimum gap (seconds) between two retriggers of the same key. */
const DEFAULT_MIN_GAP = 0.04;

/** Win popup one-shots — stopped when the celebration tier animation changes. */
const WIN_CELEBRATION_SOUND_KEYS = new Set(['bigWin', 'hugeWin', 'megaWin', 'epicWin']);

class SoundManager {
  constructor() {
    /** @type {AudioContext | null} */
    this.ctx = null;
    /** @type {Record<string, AudioBuffer>} */
    this.buffers = Object.create(null);
    /** @type {Record<string, number>} Seconds of leading silence to skip (MP3 encoder delay). */
    this._trimOffset = Object.create(null);
    this.muted = false;
    this.unlocked = false;
    /** @type {GainNode | null} */
    this._masterGain = null;
    /** @type {GainNode | null} */
    this._musicGain = null;
    /** @type {GainNode | null} */
    this._oneShotGain = null;
    /** @type {AudioBufferSourceNode | null} */
    this._bgSource = null;
    this._bgPlaying = false;
    /** @type {Set<AudioBufferSourceNode>} Active non-music sources, tracked so a
     *  new spin can hard-stop any lingering one-shots (win tier audio, symbol
     *  chimes, popup transitions) without touching the bg music loop. */
    this._activeSources = new Set();
    /** Logical key per source — must use WeakMap: Safari/WebKit often ignores
     *  expando properties on native AudioBufferSourceNode, so `src._sndKey`
     *  was always undefined and {@link stopAllSfx} killed `spinStop` too. */
    this._sourcePlayKey = new WeakMap();
    /** Per-shot GainNode for each source — must disconnect on end/stop or Android
     *  WebViews accumulate orphaned nodes in the audio graph. */
    this._sourceGain = new WeakMap();
    /** @type {Record<string, number>} ctx.currentTime of last play per key (throttle). */
    this._lastPlayedAt = Object.create(null);
    /** Final celebration tier one-shot — allowed to ring out through collect / overlay hide. */
    this._protectedWinCelebrationKey = null;
    /** Bumps when alert play is cancelled — blocks late async decode from starting. */
    this._bigWinAlertEpoch = 0;
    this._unlockListeners = null;
    this._visListener = null;
    if (typeof window !== 'undefined') {
      this._installUnlock();
      this._installVisibility();
    }
  }

  _AudioContextCtor() {
    if (typeof window === 'undefined') return null;
    return window.AudioContext || window.webkitAudioContext || null;
  }

  _ensureCtx() {
    if (this.ctx) return this.ctx;
    const Ctor = this._AudioContextCtor();
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor({ latencyHint: 'interactive' });
    } catch (_e) {
      try {
        this.ctx = new Ctor();
      } catch (_e2) {
        return null;
      }
    }
    this._masterGain = this.ctx.createGain();
    this._masterGain.gain.value = this.muted ? 0 : 1;
    this._masterGain.connect(this.ctx.destination);
    this._musicGain = this.ctx.createGain();
    this._musicGain.gain.value = MUSIC_BUS_VOLUME;
    this._musicGain.connect(this._masterGain);
    this._oneShotGain = this.ctx.createGain();
    this._oneShotGain.gain.value = ONESHOT_BUS_VOLUME;
    this._oneShotGain.connect(this._masterGain);
    return this.ctx;
  }

  _installUnlock() {
    const events = ['pointerdown', 'touchstart', 'touchend', 'mousedown', 'keydown'];
    const handler = () => {
      this._unlock();
    };
    /** capture: react before app handlers, passive: don't block scrolling. */
    const opts = { capture: true, passive: true };
    for (let i = 0; i < events.length; i++) {
      window.addEventListener(events[i], handler, opts);
    }
    this._unlockListeners = { events: events, handler: handler, opts: opts };
  }

  _detachUnlock() {
    if (!this._unlockListeners) return;
    const u = this._unlockListeners;
    for (let i = 0; i < u.events.length; i++) {
      window.removeEventListener(u.events[i], u.handler, u.opts);
    }
    this._unlockListeners = null;
  }

  _installVisibility() {
    const onVisibility = () => {
      if (!this.ctx) return;
      if (document.visibilityState === 'visible') {
        this._scheduleResume();
      } else {
        this._suspendCtx();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    /* iOS Safari also dispatches `pagehide` when entering bf-cache and `blur`
     * when switching apps — belt-and-suspenders so audio never bleeds through. */
    window.addEventListener('pagehide', () => this._suspendCtx());
    window.addEventListener('blur', () => this._suspendCtx());
    window.addEventListener('focus', () => this._scheduleResume());
  }

  _isPageActive() {
    if (typeof document === 'undefined') return false;
    if (document.visibilityState && document.visibilityState !== 'visible') return false;
    if (typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
    return true;
  }

  /**
   * Resume after a short debounce. When returning from another app, the
   * browser fires `focus` / `visibilitychange visible` on the previously
   * active game tab even if the user immediately switches to a different
   * tab. Without a debounce we'd hear a brief blast of music between the
   * resume and the subsequent suspend; this delays the resume long enough
   * for an incoming suspend to cancel it.
   */
  _scheduleResume() {
    if (this.muted) return;
    if (this._resumeTimer) {
      clearTimeout(this._resumeTimer);
    }
    this._resumeTimer = setTimeout(() => {
      this._resumeTimer = null;
      if (this.muted) return;
      if (!this._isPageActive()) return;
      this._resumeCtx();
    }, 220);
  }

  _resumeCtx() {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') {
      try {
        const r = this.ctx.resume();
        if (r && typeof r.then === 'function') r.then(undefined, () => {});
      } catch (_e) {
        /* ignore */
      }
    }
  }

  _suspendCtx() {
    if (this._resumeTimer) {
      clearTimeout(this._resumeTimer);
      this._resumeTimer = null;
    }
    if (!this.ctx) return;
    if (this.ctx.state === 'running') {
      try {
        const r = this.ctx.suspend();
        if (r && typeof r.then === 'function') r.then(undefined, () => {});
      } catch (_e) {
        /* ignore */
      }
    }
  }

  _unlock() {
    const ctx = this._ensureCtx();
    if (!ctx) return;
    this._resumeCtx();
    /** iOS 12/13 legacy unlock — playing a 1-frame silent buffer flips the
     *  internal "audio-allowed" flag inside Safari's audio engine. */
    try {
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      if (src.start) src.start(0);
      else if (src.noteOn) src.noteOn(0);
    } catch (_e) {
      /* ignore */
    }
    if (!this.unlocked) {
      this.unlocked = true;
      this._detachUnlock();
      if (!this.muted && this.buffers.bg) {
        this._startBgMusic();
      }
    }
  }

  /**
   * Fetch + decode every sound. Sequential to keep iOS memory steady; the
   * total payload is small enough that overall latency is fine.
   * Safe to call multiple times; only the first kicks off work.
   */
  preload() {
    if (this._preloadPromise) return this._preloadPromise;
    const ctx = this._ensureCtx();
    if (!ctx) {
      this._preloadPromise = Promise.resolve();
      return this._preloadPromise;
    }
    const keys = Object.keys(SOUND_PATHS);
    let chain = Promise.resolve();
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      chain = chain.then(() => this._loadOne(key));
    }
    this._preloadPromise = chain.then(() => {
      if (this.unlocked && !this.muted && this.buffers.bg) {
        this._startBgMusic();
      }
    });
    return this._preloadPromise;
  }

  /** @param {ArrayBuffer} ab */
  _looksLikeMp3(ab) {
    if (!ab || ab.byteLength < 4) return false;
    const u8 = new Uint8Array(ab, 0, Math.min(4, ab.byteLength));
    if (u8[0] === 0x49 && u8[1] === 0x44 && u8[2] === 0x33) return true; // ID3
    return u8[0] === 0xff && (u8[1] & 0xe0) === 0xe0; // MPEG frame sync
  }

  _loadOne(key) {
    const url = assetUrl('/' + SOUND_PATHS[key]);
    return fetch(url, assetFetchInit)
      .then((res) => {
        if (!res.ok) throw new Error('http ' + res.status + ' for ' + url);
        return res.arrayBuffer();
      })
      .then((ab) => {
        if (!this._looksLikeMp3(ab)) {
          throw new Error('response is not MP3 (check path/casing) for ' + url);
        }
        return this._decode(ab);
      })
      .then((buf) => {
        if (buf) {
          this.buffers[key] = buf;
          this._trimOffset[key] = this._leadingSilenceSec(buf);
        }
      })
      .catch((err) => {
        console.warn('SoundManager: failed to load ' + key + ' (' + url + ')', err);
      });
  }

  /**
   * MP3 encoder padding often becomes silence at the start of the decoded buffer.
   * Skipping it keeps one-shots (especially short landing thuds) on the visual beat.
   * @param {AudioBuffer} buffer
   * @returns {number}
   */
  _leadingSilenceSec(buffer) {
    if (!buffer || !buffer.length || !buffer.sampleRate) return 0;
    const sr = buffer.sampleRate;
    const channels = buffer.numberOfChannels;
    /** ~1.5% FS — matches the offline silence probe used for these assets. */
    const threshold = 0.015;
    const maxCheck = Math.min(buffer.length, Math.floor(sr * 0.12));
    const data = [];
    for (let c = 0; c < channels; c++) data.push(buffer.getChannelData(c));
    for (let i = 0; i < maxCheck; i++) {
      let peak = 0;
      for (let c = 0; c < channels; c++) {
        const v = Math.abs(data[c][i]);
        if (v > peak) peak = v;
      }
      if (peak > threshold) {
        return i / sr;
      }
    }
    return 0;
  }

  _decode(arrayBuffer) {
    return new Promise((resolve) => {
      if (!this.ctx) return resolve(null);
      let settled = false;
      const ok = (b) => {
        if (settled) return;
        settled = true;
        resolve(b || null);
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        resolve(null);
      };
      try {
        const p = this.ctx.decodeAudioData(arrayBuffer, ok, fail);
        if (p && typeof p.then === 'function') {
          p.then(ok, fail);
        }
      } catch (_e) {
        fail();
      }
    });
  }

  /** Master mute. Smoothly ramps to avoid clicks. */
  setMuted(mute) {
    this.muted = !!mute;
    if (this._masterGain && this.ctx) {
      const t = this.ctx.currentTime;
      const target = this.muted ? 0 : 1;
      try {
        this._masterGain.gain.cancelScheduledValues(t);
        this._masterGain.gain.setValueAtTime(this._masterGain.gain.value, t);
        this._masterGain.gain.linearRampToValueAtTime(target, t + 0.05);
      } catch (_e) {
        this._masterGain.gain.value = target;
      }
    }
    if (this.muted) {
      this._stopBgMusic();
    } else if (this.unlocked && this.buffers.bg) {
      this._startBgMusic();
    }
  }

  isMuted() {
    return this.muted;
  }

  _bufferKeyFor(key) {
    return key === 'spinStop' ? 'spinStart' : key;
  }

  /**
   * Decoded clip length in seconds (0 when not loaded yet).
   * @param {string} key
   * @returns {number}
   */
  getDurationSec(key) {
    const bufferKey = this._bufferKeyFor(key);
    const buffer = this.buffers[bufferKey];
    const d = buffer?.duration;
    return Number.isFinite(d) && d > 0 ? d : 0;
  }

  /**
   * Disconnect a one-shot source and its per-play GainNode from the graph.
   * @param {AudioBufferSourceNode} src
   */
  _disconnectOneShot(src) {
    const g = this._sourceGain.get(src);
    this._sourceGain.delete(src);
    try { src.disconnect(); } catch (_e) { /* ignore */ }
    if (g) {
      try { g.disconnect(); } catch (_e) { /* ignore */ }
    }
  }

  /**
   * Stop every in-flight one-shot for a logical sound key.
   * @param {string} key
   */
  stopSoundKey(key) {
    if (this._activeSources.size === 0) return;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      if (this._sourcePlayKey.get(src) !== key) continue;
      this._activeSources.delete(src);
      try { src.onended = null; } catch (_e) { /* ignore */ }
      try { src.stop(0); } catch (_e) { /* ignore */ }
      this._disconnectOneShot(src);
    }
  }

  /**
   * Stop the looping reel-bed clip when reels finish or a new spin starts.
   * Landing thuds ({@link stopSoundKey} `reelDefaultStop` / `reelStopper`) are left alone.
   */
  stopReelSpinSound() {
    this.stopSoundKey('reelSpin');
  }

  /**
   * @deprecated Use {@link stopReelSpinSound} — do not stop landing thuds here.
   */
  stopReelMotionSounds() {
    this.stopReelSpinSound();
  }

  /**
   * Stop the pre-popup alert sting and cancel any in-flight load/play.
   */
  stopBigWinAlert() {
    this._bigWinAlertEpoch += 1;
    this.stopSoundKey('bigWinAlert');
  }

  /**
   * Pre-big-win cabinet rumble sting — duration drives the shake timeline.
   */
  playBigWinAlert() {
    this._bigWinAlertEpoch += 1;
    const epoch = this._bigWinAlertEpoch;
    this.stopSoundKey('bigWinAlert');
    const bufferKey = 'bigWinAlert';
    if (!this.buffers[bufferKey]) {
      void this._loadOne(bufferKey).then(() => {
        if (epoch !== this._bigWinAlertEpoch) return;
        this._playOneShot('bigWinAlert', { allowOverlap: true });
      });
      return;
    }
    this._playOneShot('bigWinAlert', { allowOverlap: true });
  }

  /**
   * Trigger a one-shot sound. No-ops cleanly if muted, missing, or pre-unlock.
   * @param {string} key key in SOUND_PATHS (or `spinStop`, which aliases `spinStart`)
   * @param {{ channel?: 'sfx' | 'win' | 'music', volume?: number, minGap?: number, allowOverlap?: boolean }} [opts]
   *   `channel` is ignored for one-shots (all use the shared one-shot bus). Music
   *   only plays via the background loop, not `play('bg')`.
   */
  play(key, opts) {
    const bufferKey = this._bufferKeyFor(key);
    if (!this.buffers[bufferKey] && SOUND_PATHS[bufferKey]) {
      void this._loadOne(bufferKey).then(() => this._playOneShot(key, opts));
      return;
    }
    this._playOneShot(key, opts);
  }

  /**
   * Quick-bet strip scroll tick — rapid layered uiClick so a flick sounds like a wheel.
   * @param {{ volume?: number }} [opts]
   */
  playQuickBetWheelTick(opts) {
    const vol =
      opts && opts.volume != null && Number.isFinite(opts.volume) ? opts.volume : 0.4;
    this.play('uiClick', { allowOverlap: true, minGap: 0, volume: vol });
  }

  /**
   * @param {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin'} key
   * @returns {boolean}
   */
  isWinCelebrationKeyPlaying(key) {
    if (!WIN_CELEBRATION_SOUND_KEYS.has(key) || this._activeSources.size === 0) return false;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      if (this._sourcePlayKey.get(sources[i]) === key) return true;
    }
    return false;
  }

  /**
   * Start the celebration's final-tier popup sound if it is not already playing.
   * Stops any non-final buildup audio first; never restarts a protected final clip.
   * @param {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin'} key
   */
  ensureFinalWinCelebrationSound(key) {
    if (!WIN_CELEBRATION_SOUND_KEYS.has(key)) return;
    if (
      this._protectedWinCelebrationKey === key &&
      this.isWinCelebrationKeyPlaying(key)
    ) {
      return;
    }
    this.playWinCelebrationTierSound(key, { final: true });
  }

  /**
   * Hard-stop in-flight win popup tier sounds (big / huge / mega / epic).
   * @param {{ force?: boolean }} [opts] When false, the final-tier clip (see
   *   {@link #playWinCelebrationTierSound}) is left playing so it can finish.
   */
  stopWinCelebrationSounds(opts) {
    const force = !!(opts && opts.force);
    if (this._activeSources.size === 0) {
      if (force) this._protectedWinCelebrationKey = null;
      return;
    }
    const protectedKey = force ? null : this._protectedWinCelebrationKey;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      const k = this._sourcePlayKey.get(src);
      if (!WIN_CELEBRATION_SOUND_KEYS.has(k)) continue;
      if (!force && protectedKey && k === protectedKey) continue;
      this._activeSources.delete(src);
      try { src.onended = null; } catch (_e) { /* ignore */ }
      try { src.stop(0); } catch (_e) { /* ignore */ }
      this._disconnectOneShot(src);
    }
    if (force) this._protectedWinCelebrationKey = null;
  }

  /**
   * Switch win popup audio to match the tier animation that just started.
   * Cuts any previous tier popup sound before playing the new one.
   * @param {'bigWin' | 'hugeWin' | 'megaWin' | 'epicWin'} key
   * @param {{ final?: boolean }} [opts] When true, this clip is the celebration's
   *   last tier and is not cut by collect / overlay hide (only a new round or
   *   {@link #stopAllSfx} stops it).
   */
  playWinCelebrationTierSound(key, opts) {
    if (!WIN_CELEBRATION_SOUND_KEYS.has(key)) return;
    if (this.muted || !this._isPageActive()) return;
    const isFinal = !!(opts && opts.final);
    this.stopWinCelebrationSounds({ force: true });
    this._protectedWinCelebrationKey = isFinal ? key : null;
    this._playOneShot(key, { allowOverlap: true }, () => {
      if (this._protectedWinCelebrationKey === key) {
        this._protectedWinCelebrationKey = null;
      }
    });
  }

  /**
   * @param {string} key
   * @param {{ channel?: 'sfx' | 'win' | 'music', volume?: number, minGap?: number, allowOverlap?: boolean }} [opts]
   * @param {() => void} [onEnded]
   */
  _playOneShot(key, opts, onEnded) {
    if (this.muted) {
      onEnded?.();
      return;
    }
    if (key === 'bg') {
      onEnded?.();
      return;
    }
    const ctx = this._ensureCtx();
    if (!ctx) {
      onEnded?.();
      return;
    }
    const bufferKey = this._bufferKeyFor(key);
    const buffer = this.buffers[bufferKey];
    if (!buffer) {
      onEnded?.();
      return;
    }
    /* Stray timer in a backgrounded tab? Don't start sound and don't resume
     * the context — otherwise an autoplay / GSAP callback firing while we
     * are on another tab would re-wake the audio engine. */
    if (!this._isPageActive()) {
      onEnded?.();
      return;
    }
    this._resumeCtx();
    const now = ctx.currentTime;
    const minGap = opts && opts.minGap != null ? opts.minGap : DEFAULT_MIN_GAP;
    if (!(opts && opts.allowOverlap)) {
      const last = this._lastPlayedAt[key] || 0;
      if (now - last < minGap) return;
      this._lastPlayedAt[key] = now;
    }
    const dest = this._oneShotGain;
    try {
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      const override = opts && opts.volume;
      const vol =
        override != null && Number.isFinite(override)
          ? override
          : SOUND_VOLUMES[key] != null
            ? SOUND_VOLUMES[key]
            : DEFAULT_ONESHOT_GAIN;
      const g = ctx.createGain();
      g.gain.value = vol;
      src.connect(g);
      g.connect(dest);
      this._sourcePlayKey.set(src, key);
      this._sourceGain.set(src, g);
      this._activeSources.add(src);
      src.onended = () => {
        this._activeSources.delete(src);
        this._disconnectOneShot(src);
        onEnded?.();
      };
      const trim = this._trimOffset[bufferKey] || 0;
      /* start(when, offset): when<=0 / past ⇒ ASAP; offset skips encoder padding. */
      if (src.start) src.start(0, trim);
      else if (src.noteOn) src.noteOn(0);
    } catch (_e) {
      onEnded?.();
    }
  }

  /**
   * Hard-stop every currently playing one-shot, leaving the bg music loop alone.
   * Used when a new spin begins so leftover audio from the
   * previous round (popup tails, symbol chimes, win tier hits) cuts cleanly.
   *
   * `spinStop`, `reelDefaultStop`, and `reelStopper` are always exempt — the stop
   * button click and per-reel landings should ring out; cutting them when the next spin starts
   * sounds broken on iOS (native nodes did not retain expando keys before
   * {@link #_sourcePlayKey}).
   * Callers may pass extra `except` keys for additional carve-outs.
   *
   * @param {{ except?: string[] }} [opts]
   */
  stopAllSfx(opts) {
    if (this._activeSources.size === 0) return;
    const extra = opts && opts.except;
    const sources = Array.from(this._activeSources);
    for (let i = 0; i < sources.length; i++) {
      const src = sources[i];
      const k = this._sourcePlayKey.get(src);
      if (k === 'spinStop' || k === 'reelDefaultStop' || k === 'reelStopper') continue;
      if (extra && extra.indexOf(k) >= 0) continue;
      if (WIN_CELEBRATION_SOUND_KEYS.has(k) && k === this._protectedWinCelebrationKey) {
        this._protectedWinCelebrationKey = null;
      }
      this._activeSources.delete(src);
      try { src.onended = null; } catch (_e) { /* ignore */ }
      try { src.stop(0); } catch (_e) { /* ignore */ }
      this._disconnectOneShot(src);
    }
  }

  _startBgMusic() {
    if (this._bgPlaying || !this.buffers.bg) return;
    if (this.muted || !this.unlocked) return;
    const ctx = this._ensureCtx();
    if (!ctx) return;
    this._resumeCtx();
    try {
      const src = ctx.createBufferSource();
      src.buffer = this.buffers.bg;
      src.loop = true;
      src.connect(this._musicGain);
      const trim = this._trimOffset.bg || 0;
      if (src.start) src.start(0, trim);
      else if (src.noteOn) src.noteOn(0);
      this._bgSource = src;
      this._bgPlaying = true;
    } catch (_e) {
      /* ignore */
    }
  }

  _stopBgMusic() {
    if (!this._bgSource) return;
    try { this._bgSource.stop(0); } catch (_e) { /* ignore */ }
    try { this._bgSource.disconnect(); } catch (_e) { /* ignore */ }
    this._bgSource = null;
    this._bgPlaying = false;
  }
}

export const sound = new SoundManager();
